import { Controller } from "@hotwired/stimulus";

// Discount Leakage realignment model — live churn sliders.
//
// Each bucket card carries the raw inputs the math needs as data-* attrs:
//   data-bucket-key             "negligible" | "small" | "notable" | "steep" | "severe"
//   data-member-count           members in this bucket
//   data-canonical-total-cents  Σ target_monthly_cents for the bucket (legacy name kept for the data-* attr)
//   data-effective-total-cents  Σ effective_monthly_cents for the bucket
//
// The wrapping element optionally carries an automatic-surplus-loss value
// — the unavoidable MRR give-up from above-target members who'd be price-
// decreased on realignment. It's a constant per page load (changes when
// the operator picks a new target plan, which triggers a full reload).
//
// On slider input we mirror the math in
// `Reports::DiscountLeakage.projected_realignment` so the UI stays in
// lockstep with the server-side calc that ran on initial load.
//
//   projected_loss = round(member_count × churn% / 100)
//   stayer_ratio   = (member_count − projected_loss) / member_count
//   churner_ratio  = projected_loss / member_count
//   recovered      = round(Σ target × stayer_ratio) − round(Σ effective × stayer_ratio)
//   lost           = round(Σ effective × churner_ratio) + automatic_surplus_loss
//   net            = recovered − lost
//
// Headline totals are per-bucket sums plus the automatic surplus loss.
export default class extends Controller {
  static targets = [
    "bucket", "slider", "churnLabel",
    "projectedLoss", "bucketRecovered", "bucketLost", "bucketNet",
    "totalRecovered", "totalLost", "totalNet", "totalProjectedLoss"
  ];

  static values = {
    automaticSurplusLossCents: { type: Number, default: 0 }
  };

  recalc(event) {
    const bucketEl = event.target.closest("[data-discount-leakage-realign-target~='bucket']");
    if (!bucketEl) return;

    this.recalcBucket(bucketEl);
    this.recalcTotals();
  }

  recalcBucket(bucketEl) {
    const memberCount     = Number(bucketEl.dataset.memberCount || 0);
    const canonicalTotal  = Number(bucketEl.dataset.canonicalTotalCents || 0);
    const effectiveTotal  = Number(bucketEl.dataset.effectiveTotalCents || 0);

    const slider          = bucketEl.querySelector("input[type='range']");
    const churnPct        = Number(slider.value);

    const projectedLoss   = memberCount > 0 ? Math.round((memberCount * churnPct) / 100) : 0;
    const stayerRatio     = memberCount > 0 ? (memberCount - projectedLoss) / memberCount : 0;
    const churnerRatio    = memberCount > 0 ? projectedLoss / memberCount : 0;

    const recoveredCents  = Math.round(canonicalTotal * stayerRatio) - Math.round(effectiveTotal * stayerRatio);
    const lostCents       = Math.round(effectiveTotal * churnerRatio);
    const netCents        = recoveredCents - lostCents;

    bucketEl.querySelector("[data-discount-leakage-realign-target~='churnLabel']").textContent      = `${churnPct}%`;
    bucketEl.querySelector("[data-discount-leakage-realign-target~='projectedLoss']").textContent   = projectedLoss;
    bucketEl.querySelector("[data-discount-leakage-realign-target~='bucketRecovered']").textContent = `+${formatCents(recoveredCents)}`;
    bucketEl.querySelector("[data-discount-leakage-realign-target~='bucketLost']").textContent      = `-${formatCents(lostCents)}`;

    const netEl = bucketEl.querySelector("[data-discount-leakage-realign-target~='bucketNet']");
    netEl.textContent = `${netCents < 0 ? "-" : "+"}${formatCents(Math.abs(netCents))}`;
    setSignedClass(netEl, netCents);
  }

  recalcTotals() {
    let totalRecovered = 0;
    let totalLost      = 0;
    let totalLoss      = 0;

    this.bucketTargets.forEach((bucketEl) => {
      const memberCount     = Number(bucketEl.dataset.memberCount || 0);
      const canonicalTotal  = Number(bucketEl.dataset.canonicalTotalCents || 0);
      const effectiveTotal  = Number(bucketEl.dataset.effectiveTotalCents || 0);
      const slider          = bucketEl.querySelector("input[type='range']");
      const churnPct        = Number(slider.value);

      const projectedLoss = memberCount > 0 ? Math.round((memberCount * churnPct) / 100) : 0;
      const stayerRatio   = memberCount > 0 ? (memberCount - projectedLoss) / memberCount : 0;
      const churnerRatio  = memberCount > 0 ? projectedLoss / memberCount : 0;

      totalRecovered += Math.round(canonicalTotal * stayerRatio) - Math.round(effectiveTotal * stayerRatio);
      totalLost      += Math.round(effectiveTotal * churnerRatio);
      totalLoss      += projectedLoss;
    });

    // Above-target members get a price decrease on realignment — pure MRR
    // give-up, no churn risk. It's a constant for this target so we just
    // fold it into the headline lost on every recompute.
    totalLost += this.automaticSurplusLossCentsValue;

    const totalNet = totalRecovered - totalLost;
    this.totalRecoveredTarget.textContent     = `+${formatCents(totalRecovered)}`;
    this.totalLostTarget.textContent          = `-${formatCents(totalLost)}`;
    this.totalNetTarget.textContent           = `${totalNet < 0 ? "-" : "+"}${formatCents(Math.abs(totalNet))}`;
    setSignedClass(this.totalNetTarget, totalNet);
    this.totalProjectedLossTarget.textContent = totalLoss;
  }
}

// $X,XXX.XX from cents. Mirrors the server's format_cents_as_currency
// helper for USD-only output — we don't ship multi-currency yet.
function formatCents(cents) {
  const dollars = (cents / 100).toFixed(2);
  const [whole, frac] = dollars.split(".");
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${frac}`;
}

function setSignedClass(el, value) {
  el.classList.remove("text-red-700", "dark:text-red-300", "text-emerald-700", "dark:text-emerald-300");
  if (value < 0) {
    el.classList.add("text-red-700", "dark:text-red-300");
  } else {
    el.classList.add("text-emerald-700", "dark:text-emerald-300");
  }
}
